import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import api from '../../../constants/api';
import { isPlatformScope } from '../../../constants/tenancy';

// MySQL SUM()/COUNT() can come back as strings or null depending on the driver,
// so normalize each value before rendering it as a summary card.
const asCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? String(count) : '0';
};

export default function TopBar({ bookingStats, countOfDistancesLessThan10 }) {
  const [websiteEnq, setWebsiteEnq] = useState('0');
  const [quotationCount, setQuotationCount] = useState('0');

  useEffect(() => {
    /*
     * /website/getBookingEnquiryNumber does not exist on the API — routes/website.js
     * exposes /website/stats instead — so this card sat on a 404 and never left
     * its initial '0'. The card is labelled plainly "Website Enquiries" (unlike
     * its neighbours, which qualify themselves as Pending/Completed), so it
     * shows the enquiry total; `enquiries.unread` is there if it should ever be
     * narrowed to the unread ones.
     */
    // Public-website enquiries belong to the platform tenant only; other
    // tenants would just get a 403.
    (isPlatformScope() ? api.get('/website/stats') : Promise.resolve(null))
      .then((res) => {
        setWebsiteEnq(asCount(res?.data?.data?.enquiries?.total));
      })
      .catch((error) => {
        console.error('Failed to load website enquiry stats:', error);
        setWebsiteEnq('0');
      });

    /*
     * The quotation count cannot come from /slottablebookings/getSlotBookings:
     * that query explicitly excludes quotes (`bookings.qoute = 0 OR IS NULL`),
     * so the counter it returns is structurally always 0. Pending quotations
     * have their own endpoint, and its DataTables envelope carries the total,
     * so ask for a single row and read recordsTotal off it.
     */
    api
      .get('/quotation/getAllPendingQuotation', {
        params: { draw: 1, start: 0, length: 1, search: '' },
      })
      .then((res) => {
        const total = res?.data?.recordsTotal ?? res?.data?.recordsFiltered;
        setQuotationCount(asCount(total));
      })
      .catch((error) => {
        console.error('Failed to load pending quotation count:', error);
        setQuotationCount('0');
      });
  }, []);

  const cards = [
    ...(isPlatformScope() ? [{ label: 'Website Enquiries', value: websiteEnq }] : []),
    { label: 'Pending', value: asCount(bookingStats?.pending) },
    { label: 'Completed', value: asCount(bookingStats?.completed) },
    { label: 'Payment Pending', value: asCount(bookingStats?.paymentPending) },
    { label: 'Quotations', value: quotationCount },
    { label: 'Less than 10km', value: asCount(countOfDistancesLessThan10) },
  ];

  return (
    <div className="slot-booking-summary-stats">
      {cards.map(({ label, value }) => (
        <div className="slot-booking-summary-card" key={label}>
          <div className="slot-booking-summary-label">{label}</div>
          <div className="slot-booking-summary-value">{value}</div>
        </div>
      ))}
    </div>
  );
}

TopBar.propTypes = {
  bookingStats: PropTypes.shape({
    pending: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    completed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentPending: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  countOfDistancesLessThan10: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
